import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useLanguage } from '../lib/LanguageContext';
import { Bot, X, Send, MessageCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Floating Chatbot widget (bottom-right).
 */
export default function Chatbot() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const { data } = await api.get('/client/chatbot/history');
        const items = Array.isArray(data) ? data : (data?.history || data?.messages || []);
        setMessages(items.map((m) => ({
          role: m.role || (m.sender === 'user' ? 'user' : 'bot'),
          text: m.text || m.message || m.content || '',
          ts: m.created_at || m.timestamp || new Date().toISOString(),
        })));
      } catch {
        // No history
      } finally {
        setLoaded(true);
      }
    })();
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: 'user', text, ts: new Date().toISOString() }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await api.post('/client/chatbot', { message: text });
      const reply = data?.reply || data?.message || data?.text || t('chatbot.fallbackReply');
      setMessages((m) => [...m, { role: 'bot', text: reply, ts: new Date().toISOString(), suggestions: data?.suggestions || data?.follow_ups }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: t('chatbot.errorReply'), ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const greetings = [t('chatbot.greetWelcome')];
  const suggestions = [t('chatbot.sugBalance'), t('chatbot.sugTransfer'), t('chatbot.sugLimits'), t('chatbot.sugRib')];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t('chatbot.openLabel')}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white hover:scale-105 transition-all animate-pulse-blue"
          data-testid="chatbot-open"
        >
          <MessageCircle className="w-5 h-5"/>
          <span className="hidden sm:inline text-sm font-semibold">{t('chatbot.assistantShort')}</span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[560px] max-h-[80vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700">
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-3 text-white bg-gradient-to-r from-blue-700 to-indigo-700">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><Bot className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5">{t('chatbot.sparkles')} <Sparkles className="w-3.5 h-3.5"/></div>
              <div className="text-xs text-blue-100">{t('chatbot.online')}</div>
            </div>
            <button onClick={() => setOpen(false)} className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-white/15" aria-label={t('chatbot.closeLabel')} data-testid="chatbot-close">
              <X className="w-4 h-4"/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50 dark:bg-slate-950 scrollbar-thin">
            {messages.length === 0 && (
              <>
                <BotBubble text={greetings[0]} />
                <div className="flex flex-wrap gap-2 pl-9">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => { setInput(s); setTimeout(send, 50); }}
                      className="text-xs px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:bg-blue-50 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700">
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              m.role === 'user'
                ? <UserBubble key={i} text={m.text} />
                : <BotBubble key={i} text={m.text} suggestions={m.suggestions} onPick={(s) => { setInput(s); setTimeout(send, 50); }} />
            ))}
            {sending && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><Bot className="w-4 h-4"/></div>
                <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-white border border-slate-200 dark:bg-slate-800 dark:border-slate-700 text-sm">
                  <TypingDots />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t bg-white dark:bg-slate-900 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={t('chatbot.placeholder')}
                className="flex-1 h-10 rounded-full border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="chatbot-input"
              />
              <Button onClick={send} disabled={sending || !input.trim()} className="rounded-full h-10 w-10 p-0 bg-blue-700 hover:bg-blue-800" data-testid="chatbot-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              </Button>
            </div>
            <div className="text-[10px] text-slate-400 mt-2 text-center">{t('chatbot.autoFooter')}</div>
          </div>
        </div>
      )}
    </>
  );
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] px-3 py-2 rounded-2xl rounded-br-sm bg-blue-700 text-white text-sm shadow-sm whitespace-pre-wrap break-words">{text}</div>
    </div>
  );
}

function BotBubble({ text, suggestions, onPick }) {
  return (
    <div className="flex items-end gap-2">
      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><Bot className="w-4 h-4"/></div>
      <div className="max-w-[80%]">
        <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-white border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 text-sm whitespace-pre-wrap break-words shadow-sm">
          {text}
        </div>
        {Array.isArray(suggestions) && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => onPick && onPick(s)}
                className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }}></span>
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }}></span>
    </span>
  );
}
